import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { 
  ArrowLeftIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline'
import { fetchComparisonById } from '../services/resumeService'
import { Skeleton } from './Skeleton'
import type { ResumeComparisonRecord } from '../types'

interface Props {
  userId: string
}

export function ResumeComparisonDetailPage({ userId }: Props) {
  const { comparisonId } = useParams<{ comparisonId: string }>()
  const [comparison, setComparison] = useState<ResumeComparisonRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!comparisonId || !userId) return

    setLoading(true)
    setError(null)

    fetchComparisonById(comparisonId, userId)
      .then((data) => {
        if (!data) {
          setError('Comparison not found')
        } else {
          setComparison(data)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch comparison:', err)
        setError('Failed to load comparison')
      })
      .finally(() => setLoading(false))
  }, [comparisonId, userId])

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-emerald-700 dark:text-emerald-500'
    if (score >= 50) return 'text-amber-700 dark:text-amber-500'
    return 'text-rose-700 dark:text-rose-500'
  }

  const getScoreBg = (score: number) => {
    if (score >= 75) return 'bg-emerald-500/10 border-emerald-500/30'
    if (score >= 50) return 'bg-amber-500/10 border-amber-500/30'
    return 'bg-rose-500/10 border-rose-500/30'
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <Link
          to="/resume"
          className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Resume Analysis
        </Link>
        <div className="card p-6 space-y-6">
          <div className="border-b border-app-line pb-4 space-y-2">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-8 w-96" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-40 w-full rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    )
  }

  if (error || !comparison) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <Link
          to="/resume"
          className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Resume Analysis
        </Link>
        <div className="card p-6 space-y-6">
          <div className="border-b border-app-line pb-4">
            <p className="text-xs uppercase text-app-muted mb-1">Resume Match</p>
            <h1 className="text-2xl font-bold text-app-ink font-display">Comparison Not Found</h1>
          </div>
          <div className="rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3">
            <p className="text-sm text-rose-800 dark:text-rose-200">{error || 'This comparison does not exist or you do not have permission to view it.'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Link
        to="/resume"
        className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Resume Analysis
      </Link>
      <div className="card p-6 space-y-6">
        {/* Header */}
        <div className="border-b border-app-line pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase text-app-muted mb-1">Resume Match</p>
              <h1 className="text-2xl font-bold text-app-ink flex items-center gap-2 font-display">
                <BriefcaseIcon className="h-6 w-6 text-sky-700 dark:text-sky-500" />
                {comparison.company}
                {comparison.position_title && (
                  <span className="text-app-muted font-normal">
                    — {comparison.position_title}
                  </span>
                )}
              </h1>
              <p className="text-sm text-app-muted mt-1">
                {new Date(comparison.created_at).toLocaleDateString('pl-PL', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {comparison.resume_file_name && ` • ${comparison.resume_file_name}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/app/${comparison.application_id}`}
                className="flex items-center gap-1.5 rounded-lg border border-app-line bg-app-highlight px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-highlight transition-colors"
              >
                <BriefcaseIcon className="h-4 w-4" />
                View Application
              </Link>
            </div>
          </div>
        </div>

        {/* Match Score */}
        <div className={`rounded-xl border p-6 text-center ${getScoreBg(comparison.match_score)}`}>
          <p className="text-sm text-app-muted mb-2">Overall Match Score</p>
          <p className={`text-5xl font-bold ${getScoreColor(comparison.match_score)}`}>
            {comparison.match_score}%
          </p>
          {comparison.overall_assessment && (
            <p className="text-sm text-app-muted mt-3 max-w-md mx-auto">
              {comparison.overall_assessment}
            </p>
          )}
        </div>

        {/* Skills Match */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Matched Skills */}
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircleIcon className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
              <h3 className="font-semibold text-emerald-800 dark:text-emerald-300 font-display">
                Matched Skills ({comparison.matched_skills.length})
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {comparison.matched_skills.map((skill, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium"
                >
                  {skill}
                </span>
              ))}
              {comparison.matched_skills.length === 0 && (
                <p className="text-sm text-emerald-700 dark:text-emerald-400 italic">No direct matches found</p>
              )}
            </div>
          </div>

          {/* Missing Skills */}
          <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-800 dark:text-amber-400" />
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 font-display">
                Skills to Highlight ({comparison.missing_skills.length})
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {comparison.missing_skills.map((skill, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium"
                >
                  {skill}
                </span>
              ))}
              {comparison.missing_skills.length === 0 && (
                <p className="text-sm text-amber-800 dark:text-amber-400 italic">Great! No major gaps</p>
              )}
            </div>
          </div>
        </div>

        {/* Experience Match */}
        {comparison.experience_match_score != null && (
          <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-app-ink font-display">Experience Match</h3>
              <span className={`text-lg font-bold ${getScoreColor(comparison.experience_match_score)}`}>
                {comparison.experience_match_score}%
              </span>
            </div>
            {comparison.experience_match_feedback && (
              <p className="text-sm text-app-muted">{comparison.experience_match_feedback}</p>
            )}
          </div>
        )}

        {/* Strengths for Role */}
        {comparison.strengths_for_role.length > 0 && (
          <div className="rounded-xl border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircleIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
              <h3 className="font-semibold text-sky-800 dark:text-sky-300 font-display">Your Strengths for This Role</h3>
            </div>
            <ul className="space-y-2">
              {comparison.strengths_for_role.map((strength, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-app-ink">
                  <span className="text-sky-700 dark:text-sky-500 mt-1">•</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Gaps */}
        {comparison.gaps_for_role.length > 0 && (
          <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
            <div className="flex items-center gap-2 mb-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-app-muted" />
              <h3 className="font-semibold text-app-ink font-display">Areas to Address</h3>
            </div>
            <ul className="space-y-2">
              {comparison.gaps_for_role.map((gap, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-app-muted">
                  <span className="text-app-muted mt-1">•</span>
                  {gap}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggestions */}
        {comparison.suggestions.length > 0 && (
          <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50 dark:bg-indigo-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <LightBulbIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-semibold text-indigo-800 dark:text-indigo-300 font-display">Actionable Suggestions</h3>
            </div>
            <ul className="space-y-2">
              {comparison.suggestions.map((suggestion, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-app-ink">
                  <span className="text-indigo-700 dark:text-indigo-400 font-bold mt-0.5">{i + 1}.</span>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Interview Tips */}
        {comparison.interview_tips.length > 0 && (
          <div className="rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50 dark:bg-violet-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <AcademicCapIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              <h3 className="font-semibold text-violet-800 dark:text-violet-300 font-display">Interview Preparation Tips</h3>
            </div>
            <ul className="space-y-2">
              {comparison.interview_tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-app-ink">
                  <span className="text-violet-700 dark:text-violet-400 mt-1">✓</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
